import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { useContent } from "@/context/ContentContext";

export const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (!el) {
    window.location.href = "/" + href;
    return;
  }
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset: -80 });
  } else {
    el.scrollIntoView();
  }
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { content } = useContent();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleNav = (l) => {
    setMenuOpen(false);
    if (l.href.startsWith("#")) scrollTo(l.href);
  };

  const solid = scrolled || menuOpen;

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-[#FAF8F5]/85 backdrop-blur-xl shadow-[0_10px_40px_rgba(28,25,23,0.08)]"
          : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <button
          onClick={() => scrollTo("#top")}
          className="flex items-center gap-3 text-left"
          data-testid="navbar-logo"
        >
          {content.business.logo_url ? (
            <img src={content.business.logo_url} alt={`${content.business.name} logo`} className="h-10 w-10 rounded-full object-cover" data-testid="navbar-logo-image" />
          ) : null}
          <span>
            <span
              className={`font-display text-lg font-semibold tracking-tight transition-colors duration-500 ${
                scrolled ? "text-[#1C1917]" : "text-[#FAF8F5]"
              }`}
            >
              {content.business.name}
            </span>
            <span
              className={`block text-[10px] uppercase tracking-[0.3em] transition-colors duration-500 ${
                scrolled ? "text-[#7A2B21]" : "text-[#C5A059]"
              }`}
            >
              {content.site.tagline}
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {(content.navigation || []).map((l) =>
            l.href.startsWith("#") ? (
              <button
                key={l.href + l.label}
                onClick={() => scrollTo(l.href)}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-[#C5A059] ${
                  scrolled ? "text-[#57534E]" : "text-[#FAF8F5]/85"
                }`}
              >
                {l.label}
              </button>
            ) : (
              <Link
                key={l.href + l.label}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-[#C5A059] ${
                  scrolled ? "text-[#57534E]" : "text-[#FAF8F5]/85"
                }`}
              >
                {l.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${content.business.phone_display.replace(/\s/g, "")}`}
            data-testid="navbar-phone-link"
            className={`hidden items-center gap-2 text-sm font-semibold transition-colors duration-300 md:flex ${
              scrolled ? "text-[#1C1917]" : "text-[#FAF8F5]"
            }`}
          >
            <Phone size={15} strokeWidth={2.2} />
            {content.business.phone_display}
          </a>
          <button
            onClick={() => scrollTo("#enquire")}
            data-testid="navbar-cta-button"
            className="whitespace-nowrap rounded-full bg-[#7A2B21] px-4 py-2 text-xs font-semibold text-[#FAF8F5] transition-[background-color,transform] duration-300 hover:bg-[#5C1E16] hover:scale-[1.03] active:scale-[0.98] sm:px-5 sm:py-2.5 sm:text-sm"
          >
            Talk to Us
          </button>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            data-testid="navbar-mobile-menu-button"
            className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors duration-300 lg:hidden ${
              solid ? "text-[#1C1917] hover:bg-[#1C1917]/5" : "text-[#FAF8F5] hover:bg-[#FAF8F5]/10"
            }`}
          >
            {menuOpen ? <X size={20} strokeWidth={2.2} /> : <Menu size={20} strokeWidth={2.2} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <motion.nav
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="border-t border-[#1C1917]/5 bg-[#FAF8F5]/95 backdrop-blur-xl lg:hidden"
          data-testid="navbar-mobile-menu"
        >
          <div className="mx-auto flex max-w-7xl flex-col px-6 py-4">
            {(content.navigation || []).map((l) =>
              l.href.startsWith("#") ? (
                <button
                  key={l.href + l.label}
                  onClick={() => handleNav(l)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="border-b border-[#1C1917]/5 py-3.5 text-left text-base font-medium text-[#1C1917] transition-colors hover:text-[#7A2B21] last:border-0"
                >
                  {l.label}
                </button>
              ) : (
                <Link
                  key={l.href + l.label}
                  to={l.href}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="border-b border-[#1C1917]/5 py-3.5 text-base font-medium text-[#1C1917] transition-colors hover:text-[#7A2B21] last:border-0"
                >
                  {l.label}
                </Link>
              )
            )}
            <a
              href={`tel:${content.business.phone_display.replace(/\s/g, "")}`}
              data-testid="mobile-nav-phone-link"
              className="mt-3 flex items-center gap-2 rounded-full bg-[#1C1917] px-5 py-3 text-sm font-semibold text-[#FAF8F5]"
            >
              <Phone size={15} strokeWidth={2.2} />
              Call {content.business.phone_display}
            </a>
          </div>
        </motion.nav>
      )}
    </motion.header>
  );
};
